/**
 * OMYGO product detail — gallery counter, variant selection, add-to-bag, and
 * the moment an add lands: the piece flies to the bag, the button draws its
 * check, and the "Added to your bag" sheet slides up.
 *
 * Saved pieces used to live here. They moved to assets/omygo-saved.js when
 * hearts arrived on every product card: one store, one owner, and this page's
 * heart is just another button that file handles by delegation.
 *
 * Every behaviour here is an ENHANCEMENT. The section renders a real
 * {% form 'product' %} that posts to /cart/add, real radio inputs that carry
 * the selected option, and real prices in the markup. With this file absent or
 * failing, the page still sells: BOTH CTAs submit that one form and land on
 * the cart. That is the reason the markup is not built from JSON at runtime.
 *
 * It also writes to one element it does not own: the header's bag count, via
 * the data-bag-count / data-bag-link hooks documented in
 * sections/omygo-header.liquid. That section deliberately ships no script of
 * its own, so the page that causes the change is the page that paints it.
 */
(function () {
  'use strict';

  var ADDED_MS = 2000;

  // Long enough to read as a throw, short enough that the sheet does not keep
  // anyone waiting. The sheet opens as the piece lands.
  var FLIGHT_MS = 760;

  // A cart add usually returns in well under this. Painting "Adding" for 80ms
  // and snapping back reads as a glitch rather than as feedback, so the pending
  // state is only painted if the request outlives a beat.
  var PENDING_MS = 140;

  var root = document.querySelector('[data-omygo-product]');
  if (!root) return;

  var productId = root.getAttribute('data-product-id');
  var cartAddUrl = root.getAttribute('data-cart-add-url') || '/cart/add';
  var cartUrl = root.getAttribute('data-cart-url') || '/cart';
  var variants = readVariants();

  function readVariants() {
    var el = root.querySelector('[data-omygo-variants]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent) || [];
    } catch (err) {
      return [];
    }
  }

  // ---------------------------------------------------------------- gallery

  // The counter reads from scroll position rather than an index we increment,
  // so a flick that crosses two frames, a resize, or a deep link all stay
  // truthful. Desktop hides the counter in CSS and the mosaic does not scroll.
  (function initGallery() {
    var gallery = root.querySelector('[data-gallery]');
    var counter = root.querySelector('[data-gallery-counter]');
    if (!gallery || !counter) return;

    var frames = gallery.children;
    var total = frames.length;
    if (total < 2) return;

    var pad = function (n) {
      return (n < 10 ? '0' : '') + n;
    };
    var current = -1;
    var ticking = false;

    function update() {
      ticking = false;
      var first = frames[0];
      if (!first) return;
      // Frame width plus the flex gap, measured rather than assumed — the
      // frame is a percentage width and the gap is a token.
      var stride = frames.length > 1
        ? frames[1].offsetLeft - first.offsetLeft
        : first.offsetWidth;
      if (stride <= 0) return;

      var index = Math.round(gallery.scrollLeft / stride);
      if (index < 0) index = 0;
      if (index > total - 1) index = total - 1;
      if (index === current) return;

      current = index;
      counter.textContent = pad(index + 1) + ' / ' + pad(total);
    }

    gallery.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });

    window.addEventListener('resize', update);
    update();
  })();

  // --------------------------------------------------------------- variants

  (function initVariants() {
    var fieldsets = root.querySelectorAll('[data-option-index]');
    if (!fieldsets.length || !variants.length) return;

    var idInput = root.querySelector('[data-variant-id]');
    // Plural. The sticky bar carries a second CTA, and it used to keep an
    // enabled Add to bag after switching to a sold-out colourway.
    var addButtons = root.querySelectorAll('[data-add-button]');
    var priceEls = root.querySelectorAll('[data-price]');
    var compareEl = root.querySelector('[data-compare]');

    function selectedOptions() {
      return Array.prototype.map.call(fieldsets, function (fieldset) {
        var checked = fieldset.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    function matchVariant(options) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var same = true;
        for (var j = 0; j < options.length; j++) {
          if (v.options[j] !== options[j]) {
            same = false;
            break;
          }
        }
        if (same) return v;
      }
      return null;
    }

    function apply() {
      var options = selectedOptions();

      // Reflect the chosen value beside each option label.
      Array.prototype.forEach.call(fieldsets, function (fieldset, i) {
        var out = fieldset.querySelector('[data-option-selected]');
        if (out && options[i]) out.textContent = options[i];
      });

      var variant = matchVariant(options);
      if (!variant) return;

      if (idInput) idInput.value = variant.id;

      Array.prototype.forEach.call(priceEls, function (el) {
        el.textContent = variant.price;
      });

      if (compareEl) {
        // hasCompare is the positive difference, so 0 means no saving.
        var show = variant.hasCompare > 0;
        compareEl.hidden = !show;
        if (show) compareEl.textContent = variant.compareAt;
      }

      Array.prototype.forEach.call(addButtons, function (button) {
        button.disabled = !variant.available;
      });
      setCtaLabels(variant.available ? 'default' : 'soldout');

      // Keep the URL shareable without adding a history entry per click.
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    root.addEventListener('change', function (event) {
      if (event.target && event.target.matches('[data-option-index] input')) apply();
    });
  })();

  // ------------------------------------------------------------ cta labels

  function setCtaLabels(state) {
    var labels = root.querySelectorAll('[data-cta-label]');
    Array.prototype.forEach.call(labels, function (el) {
      var next = el.getAttribute('data-label-' + state);
      if (next) el.textContent = next;
    });
  }

  // idle | pending | done. Both CTAs move together — they are two views of one
  // form, not two buttons.
  //
  // Pending deliberately does NOT set `disabled`. Disabling the element under
  // someone's finger moves focus to the body, which reads to a screen reader as
  // the control vanishing mid-action; aria-busy says the same thing without
  // taking anything away. Double submits are held off by the `busy` guard in
  // initAddToBag instead.
  function setCtaState(state) {
    var buttons = root.querySelectorAll('[data-add-button]');
    Array.prototype.forEach.call(buttons, function (button) {
      if (state === 'idle') {
        button.removeAttribute('data-state');
        button.removeAttribute('aria-busy');
        button.removeAttribute('data-draw-check');
        return;
      }
      button.setAttribute('data-state', state);
      button.toggleAttribute('data-draw-check', state === 'done');
      if (state === 'pending') {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    });
  }

  // ----------------------------------------------------------------- press

  // WHY THIS EXISTS RATHER THAN :active ALONE.
  //
  // The section carries a `:active { transform: scale(--press-scale) }` rule
  // and it is correct — but mobile Safari does not fire :active on tap unless
  // the element has a touch listener attached. The compression therefore never
  // ran on the surface it was designed for, which is why the button read as
  // doing nothing on a phone. This adds the class on pointerdown, so the press
  // lands on touch-down rather than on the server's reply. The CSS rule stays
  // for pointer devices with scripting off.
  (function initPress() {
    var SELECTOR = '.omy-pdp__cta, .omy-pdp__save';
    var pressed = null;

    function release() {
      if (!pressed) return;
      pressed.classList.remove('is-pressed');
      pressed = null;
    }

    root.addEventListener('pointerdown', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var button = target.closest(SELECTOR);
      if (!button || button.disabled) return;
      release();
      pressed = button;
      button.classList.add('is-pressed');
    });

    // On window, not on the button: a finger that slides off before lifting
    // still has to let the button go, and pointercancel is what fires when a
    // press turns into a scroll.
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
  })();

  // ------------------------------------------------------------ bag count

  // The header renders the count server-side and owns no script. These two
  // helpers are the whole of the contract described in
  // sections/omygo-header.liquid.
  function paintBagCount(count) {
    var el = document.querySelector('[data-bag-count]');
    var link = document.querySelector('[data-bag-link]');

    if (link) {
      var key = count === 0 ? 'zero' : count === 1 ? 'one' : 'other';
      var label = link.getAttribute('data-bag-label-' + key);
      // Pluralisation is Liquid's job — it emitted all three forms. The script
      // only fills the placeholder in the one it was handed.
      if (label) link.setAttribute('aria-label', label.replace('[count]', count));
    }

    if (!el) return;
    var changed = el.textContent.trim() !== String(count);
    el.textContent = count;
    el.hidden = count === 0;
    if (changed && count > 0) {
      bump(el);
      bump(link);
    }
  }

  function bump(el) {
    if (!el) return;
    el.removeAttribute('data-bump');
    // Force a style flush, or removing and re-adding the attribute in the same
    // frame is coalesced into no change and the animation never restarts.
    void el.offsetWidth;
    el.setAttribute('data-bump', '');
    el.addEventListener('animationend', function handler() {
      el.removeAttribute('data-bump');
      el.removeEventListener('animationend', handler);
    });
  }

  // -------------------------------------------------------------- add to bag

  (function initAddToBag() {
    var form = root.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    var idInput = root.querySelector('[data-variant-id]');
    // Two nodes, deliberately: one in the buy column and one in the sticky
    // bar, because on a phone the tapped button sits at the foot of the
    // viewport and the buy column can be a screen away. Only the buy-column
    // node carries role="status", so a screen reader hears the failure once.
    var errorEls = root.querySelectorAll('[data-add-error], [data-add-error-mirror]');
    var genericError = root.getAttribute('data-error-generic') || '';
    var networkError = root.getAttribute('data-error-network') || '';

    var busy = false;
    var revert = null;
    var pendingTimer = null;

    function showError(message) {
      Array.prototype.forEach.call(errorEls, function (el) {
        // Reveal BEFORE writing the text. A hidden element is out of the
        // accessibility tree, so text written into a hidden live region can
        // land with nothing listening and never be announced at all.
        el.hidden = false;
        el.textContent = message;
      });
    }

    function clearError() {
      Array.prototype.forEach.call(errorEls, function (el) {
        el.textContent = '';
        el.hidden = true;
      });
    }

    // Everything that has to happen however the request ended.
    function settle() {
      busy = false;
      clearTimeout(pendingTimer);
      setCtaState('idle');
      setCtaLabels('default');
    }

    function succeed(counted, from) {
      setCtaState('done');
      setCtaLabels('added');
      clearTimeout(revert);
      revert = setTimeout(function () {
        setCtaState('idle');
        setCtaLabels('default');
      }, ADDED_MS);

      var landed = flyToBag(from);
      Promise.all([counted, landed]).then(function (results) {
        var count = results[0];
        if (count !== null) paintBagCount(count);
        // paintBagCount only bumps when the number moved. The bag still takes
        // the piece when it did not — a second add of a one-off piece comes
        // back capped — so the icon always answers the landing.
        bump(document.querySelector('[data-bag-link]'));
        openAddedSheet();
      });

      document.dispatchEvent(new CustomEvent('omygo:cart:added'));
    }

    // POST /cart/add.js answers with the line items it added — there is no
    // item_count anywhere in that response. One small GET gets the
    // authoritative number, and it also repairs a count left stale by another
    // tab. A failure here resolves to null and is otherwise swallowed: the add
    // succeeded, and a stale header is not worth an error message about it.
    //
    // Fetching and painting are separate so a successful add can hold the
    // paint until the flying piece reaches the bag — the number changing
    // before anything has arrived reads as the page contradicting itself.
    function fetchBagCount() {
      return fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .then(function (cart) {
          return cart && typeof cart.item_count === 'number' ? cart.item_count : null;
        })
        .catch(function () {
          return null;
        });
    }

    // The button that was tapped — the flight starts from it when the gallery
    // image is off screen, which on a phone is the usual case for the sticky
    // bar's CTA.
    var source = null;

    function submit() {
      if (busy) return;
      busy = true;
      clearError();
      clearTimeout(revert);

      pendingTimer = setTimeout(function () {
        setCtaState('pending');
        setCtaLabels('pending');
      }, PENDING_MS);

      var body = new FormData();
      body.append('id', idInput ? idInput.value : '');
      body.append('quantity', '1');

      fetch(cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: body
      })
        .then(function (res) {
          // Read the body whatever the status — a refusal is where the useful
          // sentence lives. A body that is not JSON resolves to null rather
          // than rejecting, so the two failure shapes stay distinguishable.
          return res.json().then(
            function (data) {
              return { ok: res.ok, data: data };
            },
            function () {
              return { ok: res.ok, data: null };
            }
          );
        })
        .then(function (result) {
          settle();

          var counted = fetchBagCount();

          // REFRESHED ON EVERY OUTCOME, not only on success.
          //
          // A 422 from /cart/add.js does not mean nothing was added. Asking for
          // 99 of a piece with 4 in stock returns 422 AND adds 4, with the
          // message "Only 4 items were added to your cart due to availability."
          // Refreshing only on the success branch would leave the header
          // reporting a bag that is already fuller than it says, while the page
          // showed an error. paintBagCount only animates when the number
          // actually moved, so a genuine refusal stays visually quiet. On
          // success the paint waits for the landing; see succeed().
          if (result.ok) {
            succeed(counted, source);
            return;
          }

          counted.then(function (count) {
            if (count !== null) paintBagCount(count);
          });

          if (result.data) {
            // Shopify's own description — "All 1 Tortoise are in your cart",
            // or the quantity rule that was broken. More useful and more
            // accurate than anything generic written here.
            showError(result.data.description || result.data.message || genericError);
            return;
          }

          // Not JSON at all: a redirect, a challenge page, something this code
          // cannot interpret. This is the one case where handing the browser a
          // real navigation beats guessing.
          form.submit();
        })
        .catch(function () {
          settle();
          // Network failure. The old code called form.submit() here, which
          // offline replaces the page with the browser's own error screen and
          // loses everything the member was looking at. The message stays
          // inline and the button stays tappable, so a second try costs one tap
          // instead of a reload.
          showError(networkError);
        });
    }

    // ONE handler for both CTAs. The sticky bar's button is a real submit
    // pointing back at this form through its `form` attribute, so it arrives
    // here rather than through a duplicate path of its own — and it still
    // works with this file absent.
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      source = event.submitter || null;
      submit();
    });
  })();

  // ------------------------------------------------------------ the flight

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // The frame the member is looking at: the gallery scrolls on a phone, so
  // this is the frame nearest the left edge, not simply the first.
  function currentImage() {
    var gallery = root.querySelector('[data-gallery]');
    if (!gallery) return null;
    var frames = gallery.children;
    var best = null;
    var bestDistance = Infinity;
    var left = gallery.getBoundingClientRect().left;
    for (var i = 0; i < frames.length; i++) {
      var img = frames[i].querySelector('img');
      if (!img) continue;
      var distance = Math.abs(frames[i].getBoundingClientRect().left - left);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = img;
      }
    }
    return best;
  }

  function mostlyVisible(rect) {
    var visible = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
    return rect.width > 0 && visible > rect.height * 0.5;
  }

  // Resolves when the piece reaches the bag, or at once when there is nothing
  // to fly: no image, no bag in the header, reduced motion, or no Web
  // Animations support. Never rejects — the landing is decoration on top of an
  // add that has already succeeded.
  function flyToBag(from) {
    return new Promise(function (resolve) {
      var bag = document.querySelector('[data-bag-link]');
      var img = currentImage();
      if (!bag || !img || reducedMotion() || !document.body.animate) {
        resolve();
        return;
      }

      // Start from the image when it is on screen; otherwise lift a small
      // copy out of the button that was tapped.
      var start = img.getBoundingClientRect();
      if (!mostlyVisible(start)) {
        var anchor = (from || root.querySelector('[data-add-button]')).getBoundingClientRect();
        var size = 64;
        start = {
          left: anchor.left + anchor.width / 2 - size / 2,
          top: anchor.top + anchor.height / 2 - size * 0.625,
          width: size,
          height: size * 1.25
        };
      }
      var end = bag.getBoundingClientRect();

      var dx = end.left + end.width / 2 - (start.left + start.width / 2);
      var dy = end.top + end.height / 2 - (start.top + start.height / 2);
      var shrink = 18 / start.width;

      var outer = document.createElement('div');
      outer.className = 'omy-fly';
      outer.setAttribute('aria-hidden', 'true');
      outer.style.left = start.left + 'px';
      outer.style.top = start.top + 'px';
      outer.style.width = start.width + 'px';
      outer.style.height = start.height + 'px';

      var inner = document.createElement('div');
      inner.className = 'omy-fly__inner';
      var copy = document.createElement('img');
      copy.src = img.currentSrc || img.src;
      copy.alt = '';
      inner.appendChild(copy);
      outer.appendChild(inner);
      document.body.appendChild(outer);

      // Horizontal travel eases in, vertical travel eases in-out: the piece
      // rises before it drifts across, which is what bends the line into an
      // arc rather than a straight slide.
      //
      // The keyframes carry their own easing and the timeline runs linear, so
      // the pop, the flight and the shrink each keep their own pace. With one
      // strong easing over all three, the piece was a speck by a quarter of
      // the way and the flight read as a flicker.
      outer.animate(
        [{ transform: 'translateX(0)' }, { transform: 'translateX(' + dx + 'px)' }],
        { duration: FLIGHT_MS, easing: 'cubic-bezier(0.6, 0, 0.9, 0.5)', fill: 'forwards' }
      );
      var flight = inner.animate(
        [
          // Lift: a small pop in place, so the piece is seen leaving.
          { transform: 'translateY(0) scale(1)', opacity: 1, borderRadius: '16px', offset: 0,
            easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' },
          { transform: 'translateY(0) scale(1.06)', opacity: 1, borderRadius: '16px', offset: 0.16,
            easing: 'cubic-bezier(0.45, 0, 0.25, 1)' },
          // Flight: most of the travel, still large enough to recognise.
          { transform: 'translateY(' + dy * 0.72 + 'px) scale(' + Math.max(shrink, 0.42) + ')', opacity: 1,
            borderRadius: '24px', offset: 0.74, easing: 'cubic-bezier(0.4, 0, 0.9, 0.6)' },
          // Landing: into the bag, shrinking to a point.
          { transform: 'translateY(' + dy + 'px) scale(' + shrink + ')', opacity: 0.2, borderRadius: '50%', offset: 1 }
        ],
        { duration: FLIGHT_MS, easing: 'linear', fill: 'forwards' }
      );

      var finished = false;
      function land() {
        if (finished) return;
        finished = true;
        outer.remove();
        resolve();
      }
      flight.onfinish = land;
      flight.oncancel = land;
      // A backgrounded tab can stall an animation indefinitely; the sheet and
      // the count must not wait on it.
      setTimeout(land, FLIGHT_MS + 250);
    });
  }

  // ------------------------------------------------------------ the sheet

  var sheet = root.querySelector('[data-added-sheet]');
  var sheetReturnFocus = null;

  function fillAddedSheet() {
    var media = sheet.querySelector('[data-added-media]');
    var img = currentImage();
    if (media) {
      media.textContent = '';
      if (img) {
        var copy = document.createElement('img');
        copy.src = img.currentSrc || img.src;
        copy.alt = '';
        media.appendChild(copy);
      }
    }

    // The options exactly as the page shows them, beside each option label.
    var options = sheet.querySelector('[data-added-options]');
    if (options) {
      var chosen = Array.prototype.map
        .call(root.querySelectorAll('[data-option-selected]'), function (el) {
          return el.textContent.trim();
        })
        .filter(Boolean);
      options.textContent = chosen.join(' · ');
      options.hidden = chosen.length === 0;
    }

    var price = sheet.querySelector('[data-added-price]');
    var shown = root.querySelector('[data-price]');
    if (price && shown) price.textContent = shown.textContent.trim();
  }

  function openAddedSheet() {
    if (!sheet || typeof sheet.showModal !== 'function' || sheet.open) return;
    fillAddedSheet();
    sheetReturnFocus = document.activeElement;
    sheet.removeAttribute('data-closing');
    sheet.showModal();
    sheet.setAttribute('data-draw-check', '');
  }

  function closeAddedSheet() {
    if (!sheet || !sheet.open || sheet.hasAttribute('data-closing')) return;

    var finished = false;
    function done() {
      if (finished) return;
      finished = true;
      sheet.removeAttribute('data-closing');
      sheet.removeAttribute('data-draw-check');
      sheet.close();
      if (sheetReturnFocus && document.contains(sheetReturnFocus)) {
        sheetReturnFocus.focus({ preventScroll: true });
      }
    }

    if (reducedMotion()) {
      done();
      return;
    }
    sheet.setAttribute('data-closing', '');
    sheet.addEventListener('animationend', done, { once: true });
    setTimeout(done, 400);
  }

  if (sheet) {
    sheet.addEventListener('click', function (event) {
      // A click on the dialog element itself, rather than anything inside its
      // panel, is a click on the backdrop.
      if (event.target === sheet || event.target.closest('[data-added-close]')) {
        closeAddedSheet();
      }
    });

    // Escape fires `cancel`, which would close the dialog instantly. Take it
    // over so it leaves the same way a tap does.
    sheet.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeAddedSheet();
    });
  }
})();
